import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { EntityService } from '../../../core/entity/entity.service';
import { ENTITY_TYPE_OPTIONS, EntityType } from '../../../core/entity/entity.types';
import { CompanyNameCheckComponent } from '../../../shared/company-name-check/company-name-check';

type RegNoStatus = 'unchecked' | 'checking' | 'available' | 'taken';

/**
 * Canonicalise a registration number: keep only digits and forward slashes,
 * stripping whitespace and any other characters. Mirrors the backend
 * normalize_reg_no so the value we compare and submit matches what's stored.
 */
function normalizeRegNo(value: string | null | undefined): string {
  return (value ?? '').replace(/[^0-9/]/g, '');
}

/** Reject input that contains characters other than digits, '/', and whitespace. */
function regNoFormat(control: AbstractControl): ValidationErrors | null {
  const raw = (control.value as string | null) ?? '';
  if (!raw) return null; // 'required' handles empties
  const withoutWhitespace = raw.replace(/\s/g, '');
  if (withoutWhitespace === '') return { regNoFormat: true };
  return /^[0-9/]+$/.test(withoutWhitespace) ? null : { regNoFormat: true };
}

function tradingStartNotInFuture(group: AbstractControl): ValidationErrors | null {
  const yearCtrl = group.get('tradingStartYear');
  const monthCtrl = group.get('tradingStartMonth');
  const year = yearCtrl?.value as number | null;
  const month = monthCtrl?.value as number | null;
  if (year == null || month == null) return null;

  const now = new Date();
  const inFuture =
    year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);

  if (inFuture) {
    monthCtrl?.setErrors(
      { ...(monthCtrl.errors ?? {}), tradingStartInFuture: true },
      { emitEvent: false },
    );
  } else if (monthCtrl?.hasError('tradingStartInFuture')) {
    const { tradingStartInFuture: _tradingStartInFuture, ...rest } = monthCtrl.errors!;
    monthCtrl.setErrors(Object.keys(rest).length ? rest : null, { emitEvent: false });
  }

  return inFuture ? { tradingStartInFuture: true } : null;
}

const CONSENT_TEXT_VERSION = '2026-11-01';

@Component({
  selector: 'app-create-entity',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    CompanyNameCheckComponent,
  ],
  templateUrl: './create-entity.html',
  styleUrl: './create-entity.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEntityComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly regNoStatus = signal<RegNoStatus>('unchecked');
  /** The name CIPC holds, while its question is unanswered (DOC-93 §1). */
  protected readonly nameToConfirm = signal<string | null>(null);
  private nameConfirmed = false;
  private askedFor = '';
  protected readonly isLoading = signal(false);
  protected readonly serverError = signal<string | null>(null);

  /** Template-facing alias for the shared vocabulary (see entity.types.ts). */
  protected readonly ENTITY_TYPES = ENTITY_TYPE_OPTIONS;

  protected readonly MONTHS: { value: number; label: string }[] = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  protected readonly form = this.fb.group(
    {
      name: ['', [Validators.required, Validators.maxLength(255)]],
      tradingName: ['', Validators.maxLength(255)],
      type: ['PTY' as EntityType, Validators.required],
      regNo: ['', [Validators.required, Validators.maxLength(50), regNoFormat]],
      industry: ['', [Validators.required, Validators.maxLength(255)]],
      tradingStartMonth: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(12),
      ]),
      tradingStartYear: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1900),
        Validators.max(new Date().getFullYear()),
      ]),
      consent: [false, Validators.requiredTrue],
    },
    { validators: tradingStartNotInFuture },
  );

  private attestedAt: string | null = null;

  ngOnInit(): void {
    this.form.controls.regNo.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.nameToConfirm.set(null);
        this.nameConfirmed = false;
        this.askedFor = '';
        if (this.regNoStatus() !== 'unchecked') {
          this.regNoStatus.set('unchecked');
          this.form.controls.regNo.setErrors(null);
        }
      });

    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        if (this.form.controls.tradingName.pristine) {
          this.form.controls.tradingName.setValue(val ?? '', { emitEvent: false });
        }
      });
  }

  /**
   * The name CIPC holds, once the number is known to be free. No answer changes
   * nothing and says nothing; what the person typed is never overwritten.
   */
  private lookUpCompany(regNo: string): void {
    if (this.askedFor === regNo) return;
    this.askedFor = regNo;
    this.entityService
      .companyLookup(regNo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lookup) => {
          if (normalizeRegNo(this.form.controls.regNo.value) !== lookup.reg_no) return;
          this.nameToConfirm.set(lookup.registered_name ?? null);
          this.cdr.markForCheck();
        },
        error: () => undefined,
      });
  }

  /** On *No* nothing is kept and the person corrects the number. */
  protected answerNameCheck(answer: 'YES' | 'NO'): void {
    this.nameToConfirm.set(null);
    this.nameConfirmed = answer === 'YES';
    if (answer === 'NO') document.getElementById('entityRegNo')?.focus();
  }

  protected onRegNoBlur(): void {
    if (this.form.controls.regNo.hasError('regNoFormat')) return;

    const val = normalizeRegNo(this.form.controls.regNo.value);
    if (!val) return;

    const yearMatch = val.match(/^(\d{4})\//);
    if (yearMatch) {
      const regYear = parseInt(yearMatch[1], 10);
      if (
        regYear >= 1900 &&
        regYear <= new Date().getFullYear() &&
        this.form.controls.tradingStartYear.value === null
      ) {
        this.form.controls.tradingStartYear.setValue(regYear);
      }
    }

    this.regNoStatus.set('checking');
    this.cdr.markForCheck();

    this.entityService
      .checkRegNo(val)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.available) {
            this.regNoStatus.set('available');
            this.form.controls.regNo.setErrors(null);
            this.lookUpCompany(val);
          } else {
            this.regNoStatus.set('taken');
            this.form.controls.regNo.setErrors({ taken: true });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.regNoStatus.set('unchecked');
          this.cdr.markForCheck();
        },
      });
  }

  protected onConsentChange(checked: boolean): void {
    this.attestedAt = checked ? new Date().toISOString() : null;
  }

  /**
   * Disabled only while something is in flight. An incomplete form stays clickable so
   * pressing submit outlines every missing field, rather than greying out with no reason.
   */
  protected get isSubmitDisabled(): boolean {
    return this.isLoading() || this.regNoStatus() === 'checking';
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.isSubmitDisabled || this.form.invalid || this.regNoStatus() === 'taken') return;

    this.serverError.set(null);
    this.isLoading.set(true);

    const { name, tradingName, regNo, type, industry, tradingStartMonth, tradingStartYear } =
      this.form.getRawValue();

    const payload = {
      name: name!,
      reg_no: normalizeRegNo(regNo),
      type: type! as EntityType,
      industry: industry!,
      trading_start_month: tradingStartMonth!,
      trading_start_year: tradingStartYear!,
      role: 'entity-admin' as const,
      preparer_attestation: {
        consent_text_version: CONSENT_TEXT_VERSION,
        attested_at: this.attestedAt ?? new Date().toISOString(),
      },
      ...(tradingName ? { trading_name: tradingName } : {}),
      ...(this.nameConfirmed ? { company_name_confirmed: true } : {}),
    };

    this.entityService
      .createEntity(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entity) => {
          void this.router.navigate(['/entity', entity.short_id, 'dashboard']);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          if (err.status === 409) {
            this.regNoStatus.set('taken');
            this.form.controls.regNo.setErrors({ taken: true });
          } else if (err.status === 400 && err.error?.detail) {
            this.applyServerErrors(err.error.detail as Record<string, string | string[]>);
          } else {
            this.serverError.set(
              typeof err.error?.detail === 'string'
                ? err.error.detail
                : 'Could not register your business. Please try again.',
            );
          }
          this.cdr.markForCheck();
        },
      });
  }

  private applyServerErrors(errors: Record<string, string | string[]>): void {
    const fieldMap: Record<string, string> = {
      name: 'name',
      trading_name: 'tradingName',
      reg_no: 'regNo',
      type: 'type',
      industry: 'industry',
      trading_start_month: 'tradingStartMonth',
      trading_start_year: 'tradingStartYear',
    };
    Object.entries(errors).forEach(([backendField, message]) => {
      const controlName = fieldMap[backendField] ?? backendField;
      const errorText = Array.isArray(message) ? message[0] : message;
      this.form.get(controlName)?.setErrors({ serverError: errorText });
    });
    this.cdr.markForCheck();
  }
}
